import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import MESSAGES from '../../../../common/data/constants/messages';
import { ICompany } from '../../../../common/data/dtos/company';
import Button from '../../../../components/bootstrap/Button';
import Card, { CardFooterRight, CardTabItem } from '../../../../components/bootstrap/Card';
import FormGroup from '../../../../components/bootstrap/forms/FormGroup';
import Input from '../../../../components/bootstrap/forms/Input';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '../../../../components/bootstrap/Modal';
import Spinner from '../../../../components/bootstrap/Spinner';
import { showSuccess, showWarning } from '../../../../components/extras/Notifications';
import { onlyNumbers } from '../../../../helpers/helpers';
import cepService from '../../../../services/cep-service';
import companyService from '../../../../services/company-service';

type CompanyFormValues = {
	id?: string;
	taxId: string;
	corporateName: string;
	tradeName?: string;
	stateRegistration?: string;
	riskLevel?: number;
	primaryCnae?: string;
	secondaryCnae?: string;
	zipCode: string;
	street?: string;
	number?: string;
	observation?: string;
	neighborhood?: string;
	city?: string;
	state?: string;
};

interface Props {
	isModalOpened: boolean;
	selectedCompany: ICompany | null;
	onModalClose: () => void;
	onAfterSubmit: () => void;
}

export const CompanyModal = ({ isModalOpened, selectedCompany, onModalClose, onAfterSubmit }: Props) => {
	const formik = useFormik({
		initialValues: {
			taxId: selectedCompany?.taxId || '',
			corporateName: selectedCompany?.corporateName || '',
			tradeName: selectedCompany?.tradeName || '',
			stateRegistration: selectedCompany?.stateRegistration || '',
			riskLevel: selectedCompany?.riskLevel || 0,
			primaryCnae: selectedCompany?.primaryCnae || '',
			secondaryCnae: selectedCompany?.secondaryCnae || '',
			zipCode: selectedCompany?.zipCode || '',
			street: selectedCompany?.street || '',
			number: selectedCompany?.number || '',
			observation: selectedCompany?.observation || '',
			neighborhood: selectedCompany?.neighborhood || '',
			city: selectedCompany?.city || '',
			state: selectedCompany?.state || '',
		},

		validate: (values) => {
			const errors: {
				taxId?: string;
				corporateName?: string;
				zipCode?: string;
			} = {};

			if (!values.taxId || values.taxId === '__.___.___/____-__') errors.taxId = MESSAGES.VALIDATION.REQUIRED_FIELD;

			if (!values.corporateName) errors.corporateName = MESSAGES.VALIDATION.REQUIRED_FIELD;

			if (!values.zipCode) errors.zipCode = MESSAGES.VALIDATION.REQUIRED_FIELD;

			return errors;
		},

		onSubmit: (values, { resetForm }) => {
			onFormSubmit(values);
			resetForm();
		},
	});

	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

	useEffect(() => {
		resetForm();

		if (selectedCompany) setFieldValues(selectedCompany);
	}, [isModalOpened]);

	const onFormSubmit = async (values: CompanyFormValues) => {
		if (selectedCompany) {
			setIsSubmitting(true);

			const response = await companyService.updateCompany({
				id: selectedCompany.id,
				taxId: onlyNumbers(values.taxId),
				corporateName: values.corporateName,
				tradeName: values.tradeName,
				stateRegistration: values.stateRegistration,
				riskLevel: values.riskLevel,
				primaryCnae: values.primaryCnae,
				secondaryCnae: values.secondaryCnae,
				zipCode: values.zipCode,
				street: values.street,
				observation: values.observation,
				neighborhood: values.neighborhood,
				city: values.city,
				state: values.state,
				number: values.number,
				rowVersion: selectedCompany.rowVersion,
				status: selectedCompany.status,
			});

			setIsSubmitting(false);

			if (response.error) {
				showWarning(MESSAGES.ACTION.EDIT_ERROR);
				return;
			}

			showSuccess(MESSAGES.ACTION.EDITED);
		} else {
			setIsSubmitting(true);

			const response = await companyService.createCompany({
				taxId: onlyNumbers(values.taxId),
				corporateName: values.corporateName,
				tradeName: values.tradeName,
				stateRegistration: values.stateRegistration,
				riskLevel: values.riskLevel,
				primaryCnae: values.primaryCnae,
				secondaryCnae: values.secondaryCnae,
				zipCode: values.zipCode,
				street: values.street,
				observation: values.observation,
				neighborhood: values.neighborhood,
				city: values.city,
				state: values.state,
				number: values.number,
			});

			setIsSubmitting(false);

			if (response.error) {
				showWarning(MESSAGES.ACTION.SAVE_ERROR);
				return;
			}

			showSuccess(MESSAGES.ACTION.SAVED);
		}

		resetForm();
		onAfterSubmit();
	};

	const onClose = () => {
		onModalClose();
	};

	const debounce = (func: any, wait: number | undefined) => {
		let timeout: string | number | NodeJS.Timeout | undefined;

		return function executedFunction(...args: any[]) {
			const later = () => {
				clearTimeout(timeout);
				func(...args);
			};

			clearTimeout(timeout);
			timeout = setTimeout(later, wait);
		};
	};

	const resetForm = () => {
		formik.resetForm({
			values: {
				taxId: '',
				corporateName: '',
				tradeName: '',
				stateRegistration: '',
				riskLevel: 0,
				primaryCnae: '',
				secondaryCnae: '',
				zipCode: '',
				street: '',
				number: '',
				observation: '',
				neighborhood: '',
				city: '',
				state: '',
			},
		});
	};

	const resetAddressForm = () => {
		formik.setFieldValue('zipCode', '');
		formik.setFieldValue('address', '');
		formik.setFieldValue('observation', '');
		formik.setFieldValue('neighborhood', '');
		formik.setFieldValue('city', '');
		formik.setFieldValue('state', '');
	};

	const setFieldValues = (values: ICompany) => {
		formik.setFieldValue('taxId', values.taxId || '');
		formik.setFieldValue('corporateName', values.corporateName || '');
		formik.setFieldValue('tradeName', values.tradeName || '');
		formik.setFieldValue('stateRegistration', values.stateRegistration || '');
		formik.setFieldValue('riskLevel', values.riskLevel || 0);
		formik.setFieldValue('primaryCnae', values.primaryCnae || '');
		formik.setFieldValue('secondaryCnae', values.secondaryCnae || '');
		formik.setFieldValue('zipCode', values.zipCode || '');
		formik.setFieldValue('street', values.street || '');
		formik.setFieldValue('observation', values.observation || '');
		formik.setFieldValue('neighborhood', values.neighborhood || '');
		formik.setFieldValue('city', values.city || '');
		formik.setFieldValue('state', values.state || '');
		formik.setFieldValue('number', values.number || '');
	};

	const setAddressFieldValues = (values: any) => {
		formik.setFieldValue('zipCode', values.zipCode);
		formik.setFieldValue('street', values.street);
		formik.setFieldValue('observation', values.observation);
		formik.setFieldValue('neighborhood', values.neighborhood);
		formik.setFieldValue('city', values.city);
		formik.setFieldValue('state', values.state);
	};

	const getCep = async (digits: string) => {
		if (!digits || digits.length < 8) return;

		setIsSubmitting(true);

		const { cep, error, success } = await cepService.getCep(digits);

		setIsSubmitting(false);

		if (success) {
			resetAddressForm();
			setAddressFieldValues(cep);
		} else {
			showWarning(error || MESSAGES.ACTION.GET_ERROR);
		}
	};

	return (
		<Modal isOpen={isModalOpened} setIsOpen={onClose} size={'lg'} isCentered isStaticBackdrop>
			<ModalHeader setIsOpen={onClose}>
				<ModalTitle id='company-modal'>Manutenção de empresas</ModalTitle>
			</ModalHeader>
			<ModalBody>
				<Card hasTab tabButtonColor='primary'>
					<CardTabItem id='tab-item-1' title='Dados da empresa' icon='Store'>
						<div className='row g-4'>
							<div className='col-lg-6 col-md-6 col-sm-12'>
								<FormGroup id='taxId' label='CNPJ' isFloating>
									<Input
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={formik.values.taxId}
										isValid={formik.isValid}
										isTouched={formik.touched.taxId}
										invalidFeedback={formik.errors.taxId}
										validFeedback=''
										mask='99.999.999/9999-99'
									/>
								</FormGroup>
							</div>

							<div className='col-lg-6 col-md-6 col-sm-12'>
								<FormGroup id='stateRegistration' label='Inscrição Estadual' isFloating>
									<Input onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.stateRegistration} />
								</FormGroup>
							</div>

							<div className='col-lg-12 col-md-12 col-sm-12'>
								<FormGroup id='corporateName' label='Razão Social' isFloating>
									<Input
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={formik.values.corporateName}
										isValid={formik.isValid}
										isTouched={formik.touched.corporateName}
										invalidFeedback={formik.errors.corporateName}
										validFeedback=''
									/>
								</FormGroup>
							</div>

							<div className='col-lg-12 col-md-12 col-sm-12'>
								<FormGroup id='tradeName' label='Nome Fantasia' isFloating>
									<Input onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.tradeName} />
								</FormGroup>
							</div>

							<div className='col-lg-6 col-md-6 col-sm-12'>
								<FormGroup id='primaryCnae' label='CNAE Primário' isFloating>
									<Input onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.primaryCnae} />
								</FormGroup>
							</div>

							<div className='col-lg-6 col-md-6 col-sm-12'>
								<FormGroup id='secondaryCnae' label='CNAE Secundário' isFloating>
									<Input onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.secondaryCnae} />
								</FormGroup>
							</div>

							<div className='col-lg-6 col-md-6 col-sm-12'>
								<FormGroup id='riskLevel' label='Grau de Risco' isFloating>
									<Input onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.riskLevel} />
								</FormGroup>
							</div>
						</div>
					</CardTabItem>

					<CardTabItem id='tab-item-2' title='Dados de endereço' icon='House'>
						<div className='row g-4'>
							<div className='col-lg-6 col-md-6 col-sm-12'>
								<FormGroup id='zipCode' label='CEP' isFloating>
									<Input
										onBlur={formik.handleBlur}
										value={formik.values.zipCode}
										isValid={formik.isValid}
										isTouched={formik.touched.zipCode}
										invalidFeedback={formik.errors.zipCode}
										validFeedback=''
										onChange={(e: { target: { value: string } }) => {
											formik.handleChange(e);

											if (e.target.value.length === 8) debounce(() => getCep(e.target.value), 1000)();

											if (e.target.value.length === 0) resetAddressForm();
										}}
									/>
								</FormGroup>
							</div>

							<div className='col-lg-6 col-md-6 col-sm-12'>
								<FormGroup id='street' label='Endereço' isFloating>
									<Input onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.street} />
								</FormGroup>
							</div>

							<div className='col-lg-6 col-md-6 col-sm-12'>
								<FormGroup id='neighborhood' label='Bairro' isFloating>
									<Input onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.neighborhood} />
								</FormGroup>
							</div>

							<div className='col-lg-6 col-md-6 col-sm-12'>
								<FormGroup id='number' label='Número' isFloating>
									<Input onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.number} />
								</FormGroup>
							</div>

							<div className='col-lg-6 col-md-6 col-sm-12'>
								<FormGroup id='city' label='Cidade' isFloating>
									<Input onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.city} />
								</FormGroup>
							</div>

							<div className='col-lg-6 col-md-6 col-sm-12'>
								<FormGroup id='state' label='Estado' isFloating>
									<Input onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.state} />
								</FormGroup>
							</div>

							<div className='col-lg-6 col-md-6 col-sm-12'>
								<FormGroup id='observation' label='Complemento' isFloating>
									<Input onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.observation} />
								</FormGroup>
							</div>
						</div>
					</CardTabItem>
				</Card>
			</ModalBody>
			<ModalFooter className='bg-transparent'>
				<CardFooterRight>
					<Button color='success' className='w-100' onClick={formik.handleSubmit} isDisable={isSubmitting}>
						{isSubmitting && <Spinner isSmall inButton isGrow />}
						Salvar
					</Button>
				</CardFooterRight>
			</ModalFooter>
		</Modal>
	);
};
